import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { LinkInvoiceToTransactionModal } from "components/InvoiceAction/LinkInvoiceToTransactionModal";
import { OptionDropdown } from "components/icons/optionDropdown";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { BILL_STATUS, FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { useModal } from "hooks/useModal";
import { useHistory } from "react-router-dom";
import { VendorBill } from "types/Models/vendorBills";
import { ActionItems } from "./BillActionItems";
import { useBillActions } from "./useBillActions";

type BillActionsDropdownProps = {
  bill: VendorBill;
  hideMarkAsActive?: boolean;
  isFromBillDetails?: boolean;
};

export const BillActionsDropdown = ({
  bill,
  hideMarkAsActive,
  isFromBillDetails,
}: BillActionsDropdownProps) => {
  const paymentModal = useModal();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );

  const {
    onSendRequest,
    onStatusChange,
    onDownload,
    onDelete,
    onDuplicate,
    isDownloading,
    isDeleting,
    isUpdatingStatus,
    isDuplicating,
  } = useBillActions({
    bill,
    onRecordPayment: paymentModal.open,
    isFromBillDetails,
  });

  if (!hasWritePermission) return null;

  const isLoading =
    isDownloading || isDeleting || isUpdatingStatus || isDuplicating;

  const isBill = bill.bill_document_type === FINANCIAL_RECORD_TYPE.BILL;
  const isDraft = bill.bill_payment_status === BILL_STATUS.DRAFT;

  const onEdit =
    isBill && !isDraft
      ? () =>
          history.push(
            link(`/books/bills-and-vendors/bill/${bill.uuid}`, {
              moreQuery: {
                BILL_TYPE: FINANCIAL_RECORD_TYPE.BILL,
                editflow: "true",
              },
            })
          )
      : undefined;

  return (
    <>
      <Dropdown.Root>
        <Dropdown.Trigger asChild disabled={isLoading}>
          <Button
            onClick={(e) => e.stopPropagation()}
            customType="ghost_icon"
            size="small"
            isLoading={isLoading}
            disabled={isLoading}
          >
            <span className="t-text-text-30">
              <OptionDropdown />
            </span>
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end" onClick={(e) => e.stopPropagation()}>
            <ActionItems
              bill={bill}
              onDelete={onDelete}
              onDownload={onDownload}
              onSendRequest={onSendRequest}
              onStatusChange={onStatusChange}
              onDuplicate={onDuplicate}
              onEdit={onEdit}
              hideMarkAsActive={hideMarkAsActive}
            />
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <LinkInvoiceToTransactionModal
        type="BILL"
        invoiceId={bill.uuid}
        isOpen={paymentModal.isOpen}
        close={paymentModal.close}
      />
    </>
  );
};
